import { adminRecords } from "./adminRecords";
import DraftEditDiffButton from "./DraftEditDiffButton";
import CompletionEngine from "./CompletionEngine";
import InvocationBilling, { skippedCompletionIds } from "./InvocationBilling";
import * as Sentry from "@sentry/react";
import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router-dom";
import Auth from "../Auth";
import {
  getCompletionAdminSummary,
  humanizeCompletionFunctionName,
  isSemanticallyEmptyCompletionOutput,
} from "../../../src/shared/completionAdminSummary";
import JSONViewer from "../components/Admin/JSONViewer";
import { useToast } from "../components/ToastWrapper";
import { getLedgerDisplayInfo } from "../models/LedgerEntriesDisplay";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../sudokit/components/Dialog";
import { SudoButton } from "../sudokit/components/SudoButton";
import { Textarea } from "../sudokit/components/Textarea";
import CopyToClipboardButton from "./CopyToClipboardButton.tsx";
import StreamReplay from "./StreamReplay";

const sudowriteFeatures = [
  { value: "", label: "Select Sudowrite feature..." },
  { value: "write-story-engine", label: "Write (Story Engine)" },
  { value: "describe", label: "Describe" },
  { value: "generate-scene", label: "Generate Scene" },
  { value: "draft-generator", label: "Draft" },
  { value: "character-generator", label: "Character Generator" },
  { value: "brainstorm", label: "Brainstorm" },
  { value: "rewrite", label: "Rewrite" },
  { value: "expand", label: "Expand" },
  { value: "twist", label: "Twist" },
  { value: "canvas", label: "Canvas" },
  { value: "other-ai-tool", label: "Other AI Tool" },
];

var dateFormat = require("dateformat");

const fetchCompletionsByIds = async (completionIds = []) => {
  if (!Array.isArray(completionIds) || completionIds.length === 0) return [];
  const data = await Auth.fetchWithIdToken('/api/admin/completions/by-ids', {
    completionIds,
  });
  if (!data.success) {
    throw new Error(data.error || "Failed to fetch completions");
  }
  return data.completions || [];
};

const Invocation = ({ userCache }) => {
  // MINIMAL state only
  const [invocation, setInvocation] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Basic invocation data - loaded immediately
  const [user, setUser] = useState(null);
  const [invocationSource, setInvocationSource] = useState(null);
  const [aiFunctionName, setAiFunctionName] = useState("");

  // Heavy data - loaded on demand
  const [completions, setCompletions] = useState([]);
  const [completionsLoading, setCompletionsLoading] = useState(false);
  const [invocationContext, setInvocationContext] = useState({});
  const [expandedCompletions, setExpandedCompletions] = useState(new Set());
  const [expandedPrompts, setExpandedPrompts] = useState(new Set());
  const [expandedOutputs, setExpandedOutputs] = useState(new Set());
  const [showContext, setShowContext] = useState(false);
  const [billingContext, setBillingContext] = useState(null);
  const [billingContextLoading, setBillingContextLoading] = useState(true);
  const [billingContextError, setBillingContextError] = useState(null);
  const [planExecutionCompletions, setPlanExecutionCompletions] = useState([]);
  const [planExecutionCompletionsLoading, setPlanExecutionCompletionsLoading] = useState(false);
  const [planExecutionBillingContext, setPlanExecutionBillingContext] = useState(null);
  const [planExecutionBillingContextLoading, setPlanExecutionBillingContextLoading] = useState(false);
  const [planExecutionBillingContextError, setPlanExecutionBillingContextError] = useState(null);

  // Analysis form state - Professional naming for CS investigation
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [featureContext, setFeatureContext] = useState(""); // New field for Sudowrite feature
  const [expectedBehavior, setExpectedBehavior] = useState("");
  const [actualBehavior, setActualBehavior] = useState("");
  const [userNotes, setUserNotes] = useState("");
  const [analysisResult, setAnalysisResult] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Quick Edit restore state
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoredCardId, setRestoredCardId] = useState(null);
  const [restoredDocumentId, setRestoredDocumentId] = useState(null);
  const [restoreDocumentId, setRestoreDocumentId] = useState("");
  const [restoreCompletionId, setRestoreCompletionId] = useState("");
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreError, setRestoreError] = useState("");

  let { id } = useParams();

  // React Router can reuse the same <Invocation> component instance across navigations
  // between /admin/invocation/A and /admin/invocation/B — the restore state would
  // otherwise persist and show "Restored ✓" / card details from the previous invocation
  // on the new page. Wipe it whenever the route id changes.
  useEffect(() => {
    setIsRestoring(false);
    setRestoredCardId(null);
    setRestoredDocumentId(null);
    setRestoreDocumentId("");
    setRestoreCompletionId("");
    setRestoreDialogOpen(false);
    setRestoreError("");
  }, [id]);

  const { setToast } = useToast();

  // Custom component for better markdown spacing
  const MarkdownWrapper = ({ children, className = "" }) => (
    <div
      className={`prose prose-sm max-w-none ${className}`}
      style={{
        "--tw-prose-body": "rgb(55 65 81)",
        "--tw-prose-p": "rgb(55 65 81)",
      }}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .prose p {
            margin-bottom: 1rem !important;
            line-height: 1.6 !important;
            display: block !important;
          }
          .prose ul, .prose div ul {
            list-style-type: disc !important;
            padding-left: 1.5rem !important;
            margin-bottom: 1rem !important;
            margin-top: 0.5rem !important;
            display: block !important;
          }
          .prose ol, .prose div ol {
            list-style-type: decimal !important;
            padding-left: 1.5rem !important;
            margin-bottom: 1rem !important;
            margin-top: 0.5rem !important;
            display: block !important;
          }
          .prose li, .prose div li {
            margin-bottom: 0.5rem !important;
            line-height: 1.6 !important;
            padding-left: 0.25rem !important;
            display: list-item !important;
            list-style-position: outside !important;
          }
          .prose ul ul, .prose div ul ul {
            list-style-type: circle !important;
            margin-top: 0.25rem !important;
            margin-bottom: 0.25rem !important;
            padding-left: 1.25rem !important;
          }
          .prose ul ul ul, .prose div ul ul ul {
            list-style-type: square !important;
          }
          .prose li p, .prose div li p {
            margin-bottom: 0.25rem !important;
            margin-top: 0 !important;
            display: inline !important;
          }
          .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6,
          .prose div h1, .prose div h2, .prose div h3, .prose div h4, .prose div h5, .prose div h6 {
            margin-top: 1.5rem !important;
            margin-bottom: 0.75rem !important;
            font-weight: 600 !important;
          }
          .prose h3, .prose div h3 {
            font-size: 1.125rem !important;
            font-weight: 600 !important;
            color: rgb(55 65 81) !important;
          }
          .prose strong, .prose b, .prose div strong, .prose div b {
            font-weight: 700 !important;
            color: rgb(17 24 39) !important;
          }
          .prose em, .prose i, .prose div em, .prose div i {
            font-style: italic !important;
          }
          .prose code, .prose div code {
            background-color: rgb(243 244 246) !important;
            padding: 0.125rem 0.25rem !important;
            border-radius: 0.25rem !important;
            font-size: 0.875em !important;
            font-family: ui-monospace, 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace !important;
          }
          /* Ensure ReactMarkdown elements are properly styled */
          .prose > div ul, .prose > div ol {
            list-style-type: disc !important;
            margin-left: 0 !important;
            padding-left: 1.5rem !important;
          }
          .prose > div ol {
            list-style-type: decimal !important;
          }
        `,
        }}
      />
      {children}
    </div>
  );

  // Simple analysis function - no complex dependencies
  const analyzeInvocation = async () => {
    if (!invocation) {
      setToast("No invocation data available");
      return;
    }

    setIsAnalyzing(true);
    setAnalysisResult("");

    try {
      let analysisText = "";

      await Auth.streamWithIdToken(
        "/api/admin/analyze-invocation",
        {
          invocationId: invocation.id,
          featureContext,
          expectedBehavior,
          actualBehavior,
          userNotes,
        },
        null,
        (chunk) => {
          if (chunk.text) {
            analysisText += chunk.text;
            setAnalysisResult(analysisText);
          }
        },
        () => {
          setIsAnalyzing(false);
        },
        (error) => {
          Sentry.captureException(error);
          setToast("Failed to analyze invocation: " + error.message);
          setIsAnalyzing(false);
        }
      );
    } catch (error) {
      Sentry.captureException(error);
      setToast("Failed to analyze invocation: " + error.message);
      setIsAnalyzing(false);
    }
  };

  const isQuickEditInvocation = useMemo(() => {
    const src = invocationSource || "";
    return src.includes("commandMenuPlugins/instruct/");
  }, [invocationSource]);

  const restoreToCards = async () => {
    if (!invocation) {
      setRestoreError("No invocation data available");
      return;
    }
    setRestoreError("");
    setIsRestoring(true);
    try {
      const body = {};
      const overrideDocumentId = restoreDocumentId.trim();
      if (overrideDocumentId) {
        body.documentId = overrideDocumentId;
      }
      if (restoreCompletionId) {
        body.completionId = restoreCompletionId;
      }
      const data = await Auth.fetchWithIdToken(`/api/admin/invocations/${invocation.id}/restore-to-cards`, body);
      if (data.success) {
        setRestoredCardId(data.cardId);
        setRestoredDocumentId(data.documentId || null);
        setToast(data.alreadyExisted ? `Card already exists for this invocation: ${data.cardId}` : `Card created: ${data.cardId}`);
      } else {
        setRestoreError(data.error || "Unknown error");
      }
    } catch (error) {
      Sentry.captureException(error);
      setRestoreError(error.message);
    } finally {
      setIsRestoring(false);
    }
  };

  // Helper components for the analysis display

  const CopyButton = ({ text, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setToast(`${label} copied to clipboard`);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <SudoButton
        variant="tertiary"
        size="small"
        onClick={handleCopy}
      >
        {copied ? "Copied!" : `Copy ${label}`}
      </SudoButton>
    );
  };

  // Single effect to initialize everything
  useEffect(() => {
    const loadCompletions = async (completionIds) => {
      setCompletionsLoading(true);
      try {
        const fetched = await fetchCompletionsByIds(completionIds);
        setCompletions(fetched);
      } catch (error) {
        Sentry.captureException(error, {
          extra: { completionIds },
        });
        setCompletions([]);
      } finally {
        setCompletionsLoading(false);
      }
    };

    // Simple immediate ready state
    setIsReady(true);

    // Simple data fetch after a delay
    setTimeout(async () => {
      try {
        const doc = await adminRecords.collection("invocations").doc(id).get();
        if (doc.exists) {
          setInvocation(doc);

          // Load essential data immediately
          userCache.fetchIds([doc.get("userId")]).then(() => {
            setUser(userCache.getUser(doc.get("userId")));
          });

          setInvocationSource(doc.get("source"));
          setInvocationContext(doc.get("context") || {});

          // Load completions immediately
          const completionIds = doc.get("completionIds");
          if (completionIds && completionIds.length > 0) {
            loadCompletions(completionIds);
          }
        } else {
          setNotFound(true);
        }
      } catch (error) {
        Sentry.captureException(error);
        setNotFound(true);
      }
    }, 100);
  }, [id, userCache]); // Only depend on ID and userCache

  useEffect(() => {
    let cancelled = false;

    const loadBillingContext = async () => {
      setBillingContextLoading(true);
      setBillingContextError(null);
      setBillingContext(null);
      try {
        const data = await Auth.fetchWithIdToken(`/api/admin/billing-context/${id}`, {});
        if (cancelled) return;
        if (data.success) {
          setBillingContext(data.billingContext || null);
        } else {
          setBillingContextError(data.error || "Unable to load billing context");
        }
      } catch (error) {
        if (!cancelled) {
          Sentry.captureException(error, {
            tags: { invocationId: id },
            extra: { context: "billing_context_fetch" },
          });
          setBillingContextError(error.message);
        }
      } finally {
        if (!cancelled) {
          setBillingContextLoading(false);
        }
      }
    };

    loadBillingContext();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const planExecutionInvocationId = useMemo(() => {
    if (!invocation) return null;
    const value = invocation.get("metaData")?.agenticChat?.planExecutionInvocationId;
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }, [invocation]);

  useEffect(() => {
    let cancelled = false;

    const resetPlanExecutionData = () => {
      if (cancelled) return;
      setPlanExecutionCompletions([]);
      setPlanExecutionBillingContext(null);
      setPlanExecutionBillingContextError(null);
      setPlanExecutionCompletionsLoading(false);
      setPlanExecutionBillingContextLoading(false);
    };

    const loadPlanExecutionData = async () => {
      if (!planExecutionInvocationId || planExecutionInvocationId === id) {
        resetPlanExecutionData();
        return;
      }

      setPlanExecutionCompletionsLoading(true);
      setPlanExecutionBillingContextLoading(true);
      setPlanExecutionBillingContextError(null);

      try {
        const [doc, billingData] = await Promise.all([
          adminRecords.collection("invocations").doc(planExecutionInvocationId).get(),
          Auth.fetchWithIdToken(`/api/admin/billing-context/${planExecutionInvocationId}`, {}),
        ]);

        if (cancelled) return;

        if (doc.exists) {
          const completionIds = doc.get("completionIds");
          const fetched = await fetchCompletionsByIds(completionIds);
          if (!cancelled) {
            setPlanExecutionCompletions(fetched);
          }
        } else {
          setPlanExecutionCompletions([]);
        }

        if (!cancelled) {
          if (billingData.success) {
            setPlanExecutionBillingContext(billingData.billingContext || null);
          } else {
            setPlanExecutionBillingContextError(billingData.error || "Unable to load plan execution billing context");
          }
        }
      } catch (error) {
        if (!cancelled) {
          Sentry.captureException(error, {
            tags: { invocationId: planExecutionInvocationId },
            extra: { context: "plan_execution_data_fetch" },
          });
          setPlanExecutionBillingContextError(error.message);
          setPlanExecutionCompletions([]);
        }
      } finally {
        if (!cancelled) {
          setPlanExecutionCompletionsLoading(false);
          setPlanExecutionBillingContextLoading(false);
        }
      }
    };

    loadPlanExecutionData();

    return () => {
      cancelled = true;
    };
  }, [id, planExecutionInvocationId]);

  // Set AI function name when source is available
  useEffect(() => {
    if (invocationSource) {
      const fakeEntry = { source: invocationSource, amount: 0, type: "consumption" };
      const displayInfo = getLedgerDisplayInfo(fakeEntry);
      const name = displayInfo?.largeText || invocationSource;
      setAiFunctionName(name);
    }
  }, [invocationSource]);

  const toggleCompletionExpanded = (completionId) => {
    setExpandedCompletions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(completionId)) {
        newSet.delete(completionId);
      } else {
        newSet.add(completionId);
      }
      return newSet;
    });
  };

  const togglePromptExpanded = (completionId) => {
    setExpandedPrompts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(completionId)) {
        newSet.delete(completionId);
      } else {
        newSet.add(completionId);
      }
      return newSet;
    });
  };

  const toggleOutputExpanded = (completionId) => {
    setExpandedOutputs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(completionId)) {
        newSet.delete(completionId);
      } else {
        newSet.add(completionId);
      }
      return newSet;
    });
  };

  const billingStatusStyles = {
    charged: "bg-green-100 text-green-800 border border-green-200",
    no_charge: "bg-yellow-100 text-yellow-800 border border-yellow-200",
    failed: "bg-red-100 text-red-800 border border-red-200",
    pending: "bg-gray-100 text-gray-600 border border-gray-200",
  };

  const renderBillingBadge = () => {
    if (billingContextLoading) {
      return <span className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5">Loading…</span>;
    }
    if (billingContextError) {
      return <span className="text-xs text-red-600 bg-red-100 rounded px-2 py-0.5">Error</span>;
    }
    const context = billingContext;
    if (!context) {
      return <span className="text-xs px-2 py-0.5 rounded border border-dashed text-gray-400">Pending</span>;
    }
    const status = context.status || "pending";
    const label = status === "failed" ? "AUTO-REFUND" : status.replace(/_/g, " ").toUpperCase();
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${billingStatusStyles[status] || billingStatusStyles.pending}`}>
        {label}
      </span>
    );
  };

  const totalCreditsUsed = useMemo(() => {
    if (!Array.isArray(completions) || completions.length === 0) {
      return 0;
    }
    return completions.reduce((sum, completion) => sum + (completion.credit_cost || 0), 0);
  }, [completions]);

  const totalProviderCost = useMemo(() => {
    if (!Array.isArray(completions) || completions.length === 0) {
      return 0;
    }
    return completions.reduce((sum, completion) => sum + (completion.cost || 0), 0);
  }, [completions]);

  // Image invocations (character portraits, Visualize) record no completions, so the per-completion
  // sums above are 0. Fall back to the amount the billing context actually charged.
  const hasCompletions = Array.isArray(completions) && completions.length > 0;
  const creditsUsed = hasCompletions ? totalCreditsUsed : billingContext?.credit_cost ?? 0;
  const providerCost = hasCompletions ? totalProviderCost : billingContext?.provider_cost ?? 0;
  const skippedBillingIds = skippedCompletionIds(
    hasCompletions ? completions : planExecutionCompletions,
    hasCompletions ? billingContext : planExecutionBillingContext,
  );

  const characterPortrait = invocation?.get("metaData")?.characterPortrait ?? null;

  const planExecutionCreditsUsed = useMemo(() => {
    if (!Array.isArray(planExecutionCompletions) || planExecutionCompletions.length === 0) {
      return 0;
    }
    return planExecutionCompletions.reduce((sum, completion) => sum + (completion.credit_cost || 0), 0);
  }, [planExecutionCompletions]);

  const planExecutionProviderCost = useMemo(() => {
    if (!Array.isArray(planExecutionCompletions) || planExecutionCompletions.length === 0) {
      return 0;
    }
    return planExecutionCompletions.reduce((sum, completion) => sum + (completion.cost || 0), 0);
  }, [planExecutionCompletions]);

  const hasCrossInvocationPlanData = Boolean(
    planExecutionInvocationId &&
    planExecutionInvocationId !== id &&
    (
      planExecutionCompletions.length > 0
      || planExecutionBillingContext
      || planExecutionCompletionsLoading
      || planExecutionBillingContextLoading
    )
  );

  const formatDateTime = (value) => {
    if (!value) {
      return "—";
    }
    const asDate = new Date(value);
    if (Number.isNaN(asDate.getTime())) {
      return value;
    }
    return asDate.toLocaleString();
  };

  const agenticChatId = useMemo(() => {
    if (!invocation) {
      return null;
    }
    const meta = invocation.get("metaData");
    const fromMeta = meta?.agenticChat?.chatId;
    if (typeof fromMeta === "string" && fromMeta.trim()) {
      return fromMeta.trim();
    }
    const ctx = invocation.get("context");
    if (ctx && typeof ctx === "object" && !Array.isArray(ctx)) {
      const fromCtx = ctx.chatId ?? ctx.chat_id;
      if (typeof fromCtx === "string" && fromCtx.trim()) {
        return fromCtx.trim();
      }
    }
    return null;
  }, [invocation]);
  const historyCompaction = useMemo(() => {
    if (!invocation) return null;
    return invocation.get("metaData")?.agenticChat?.historyCompaction ?? null;
  }, [invocation]);

  const showInvocationGenerationType = Boolean(aiFunctionName)
    && !completionsLoading
    && !planExecutionCompletionsLoading
    && completions.length === 0
    && planExecutionCompletions.length === 0;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {!isReady && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-500 text-lg">Loading page...</p>
          </div>
        </div>
      )}

      {isReady && notFound && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-xl font-semibold text-red-800">Invocation not found</h2>
        </div>
      )}

      {isReady && !invocation && !notFound && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-500 text-lg">Loading invocation...</p>
          </div>
        </div>
      )}

      {isReady && invocation && !notFound && (
        <div className="space-y-6">
          {/* Header Section - Essential Info Only */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex justify-between items-center w-full gap-3 mb-4">
              <div className="flex items-center gap-4">
                <h1 className="text-2xl font-bold text-gray-700 mb-0 flex items-center gap-2">
                  Invocation:{" "}
                  <CopyToClipboardButton
                    textToCopy={invocation.id}
                    className="text-mono text-2xl"
                  />
                </h1>
                <CopyToClipboardButton
                  textToCopy={`${window?.location?.origin}/admin/invocation/${invocation.id}`}
                  successMessage="Copied invocation link to clipboard"
                  tooltipText="Copy Link"
                  useIcon
                />
              </div>
              <div className="flex items-center gap-2">
                {isQuickEditInvocation && (
                  <Dialog
                    open={restoreDialogOpen}
                    onOpenChange={(open) => {
                      setRestoreDialogOpen(open);
                      if (!open) {
                        setRestoreError("");
                      }
                    }}
                  >
                    <DialogTrigger asChild>
                      <SudoButton
                        variant="secondary"
                        size="default"
                        disabled={completionsLoading}
                      >
                        {restoredCardId ? "Restored ✓" : "Restore to Cards"}
                      </SudoButton>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Restore Quick Edit to Cards</DialogTitle>
                        <p className="text-sm text-gray-600 mt-2">
                          Creates a new card for the user containing this Quick Edit's AI output. Non-destructive — adds a card; doesn't change the document.
                        </p>
                      </DialogHeader>
                      <div className="space-y-3 py-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Source <span className="font-normal text-gray-400">(optional override)</span>
                          <select
                            value={restoreCompletionId}
                            onChange={(e) => setRestoreCompletionId(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-cta max-w-[90%]"
                            disabled={isRestoring}
                          >
                            <option value="">Auto — use chunk log if available</option>
                            {completions.map((completion, i) => {
                              const firstText = Array.isArray(completion.completions)
                                ? completion.completions.find((t) => typeof t === "string" && t.trim().length > 0)
                                : null;
                              const preview = firstText ? firstText.replace(/\s+/g, " ").slice(0, 80) : "(empty)";
                              const label = `Completion ${i + 1}${completion.functionName ? ` (${completion.functionName})` : ""}: ${preview}${preview.length >= 80 ? "…" : ""}`;
                              return (
                                <option
                                  key={completion.id}
                                  value={completion.id}
                                >
                                  {label}
                                </option>
                              );
                            })}
                          </select>
                          <span className="block mt-1 text-xs text-gray-500">
                            For older invocations without a chunk log, pick the completion that contains the actual prose (usually the last one).
                          </span>
                        </label>
                        <label className="block text-sm font-medium text-gray-700">
                          Document ID <span className="font-normal text-gray-400">(optional override)</span>
                          <input
                            type="text"
                            value={restoreDocumentId}
                            onChange={(e) => setRestoreDocumentId(e.target.value)}
                            placeholder="Leave blank to use the document from the chunk log"
                            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-cta max-w-[90%]"
                            disabled={isRestoring}
                          />
                          <span className="block mt-1 text-xs text-gray-500">
                            The server reconstructs the original prose from the streamed chunks and resolves the document automatically. Only fill this in if you need to route the card to a different document.
                          </span>
                        </label>
                        {restoreError && (
                          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                            {restoreError}
                          </div>
                        )}
                        {restoredCardId && (
                          <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-2 space-y-1">
                            <div className="flex items-center gap-2">
                              <span>Card ID:</span>
                              <CopyToClipboardButton
                                textToCopy={restoredCardId}
                                successMessage="Card ID copied"
                                tooltipText="Copy Card ID"
                                className="text-xs font-mono"
                              />
                            </div>
                            {restoredDocumentId && (
                              <div className="flex items-center gap-2">
                                <span>Attached to document:</span>
                                <CopyToClipboardButton
                                  textToCopy={restoredDocumentId}
                                  successMessage="Document ID copied"
                                  tooltipText="Copy Document ID"
                                  className="text-xs font-mono"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <DialogFooter>
                        <SudoButton
                          variant="tertiary"
                          onClick={() => setRestoreDialogOpen(false)}
                        >
                          Close
                        </SudoButton>
                        <SudoButton
                          variant="primary"
                          onClick={restoreToCards}
                          disabled={isRestoring}
                        >
                          {isRestoring ? "Restoring…" : "Restore"}
                        </SudoButton>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
                <Dialog
                  open={analysisDialogOpen}
                  onOpenChange={setAnalysisDialogOpen}
                >
                  <DialogTrigger asChild>
                    <SudoButton
                      variant="secondary"
                      size="default"
                      disabled={isAnalyzing}
                    >
                      🕵️ Sherlock Investigation
                    </SudoButton>
                  </DialogTrigger>
                  <DialogContent className="max-w-[90vw] h-[90vh] overflow-hidden flex flex-col justify-between">
                    <DialogHeader className="mb-4">
                      <DialogTitle className="flex items-center gap-2">
                        🕵️ Sherlock Investigation Console
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-app-primary-cta bg-app-primary-cta-transparent">Technical Diagnostics</span>
                      </DialogTitle>
                      <p className="text-sm text-gray-600 mt-2">
                        Sherlock analyzes invocation data with deep Sudowrite expertise to identify technical issues, provide evidence-based diagnostics, and recommend specific solutions for CS and
                        Engineering teams.
                      </p>
                    </DialogHeader>

                    <div className="flex-grow grid grid-cols-1 md:grid-cols-3 gap-4 overflow-hidden h-full">
                      <div className="flex flex-col gap-4 h-full overflow-y-auto">
                        <div className="flex flex-col gap-4 md:col-span-1 text-left">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Feature/Tool Context</label>
                            <select
                              value={featureContext}
                              onChange={(e) => setFeatureContext(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-app-primary-cta"
                            >
                              {sudowriteFeatures.map((feature) => (
                                <option
                                  key={feature.value}
                                  value={feature.value}
                                >
                                  {feature.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">User's Intended Workflow</label>
                            <Textarea
                              value={expectedBehavior}
                              onChange={(e) => setExpectedBehavior(e.target.value)}
                              placeholder="e.g., 'User clicked Write to generate 3 character description cards..'"
                              className="w-full min-h-[80px]"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Observed Technical Behavior</label>
                            <Textarea
                              value={actualBehavior}
                              onChange={(e) => setActualBehavior(e.target.value)}
                              placeholder="e.g., 'Only 1 completion returned, truncated at 150 tokens..'"
                              className="w-full min-h-[80px]"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">CS Investigation Notes</label>
                            <Textarea
                              value={userNotes}
                              onChange={(e) => setUserNotes(e.target.value)}
                              placeholder="e.g., 'User is Pro subscriber, using Chrome, issue occurs consistently..'"
                              className="w-full min-h-[80px]"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col col-span-2 bg-gray-50 rounded-lg p-4 overflow-y-auto">
                        {analysisResult ? (
                          <div className="text-left border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-lg font-semibold text-gray-800">{isAnalyzing ? "🕵️ Sherlock is on the case..." : "🕵️ Investigation Complete"}</h4>
                              {!isAnalyzing && (
                                <CopyButton
                                  text={analysisResult}
                                  label="Full Analysis"
                                />
                              )}
                            </div>
                            <MarkdownWrapper>
                              <ReactMarkdown>{analysisResult}</ReactMarkdown>
                            </MarkdownWrapper>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <div className="text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                              <p className="text-md">{isAnalyzing ? "🕵️ Sherlock is on the case..." : "🕵️ Sherlock is ready to investigate"}</p>
                              <p className="text-sm text-gray-400 mt-2">{isAnalyzing ? "Looking for clues 🔎 and sniffing out red herrings 🐟..." : "Fill out the case details and run 🕵️ Sherlock"}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <DialogFooter className="mt-6">
                      <SudoButton
                        variant="tertiary"
                        onClick={() => setAnalysisDialogOpen(false)}
                      >
                        Close
                      </SudoButton>
                      <SudoButton
                        variant="primary"
                        onClick={analyzeInvocation}
                        disabled={isAnalyzing}
                      >
                        {isAnalyzing ? "🕵️ Sherlock is on the case..." : "Run 🕵️ Sherlock"}
                      </SudoButton>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-2 items-center text-md text-gray-600">
              <div className="flex flex-wrap items-center gap-x-1 min-h-[1.5rem]">
                <span className="font-medium">Created:</span>
                <span>{dateFormat(invocation.get("createdAt").toDate(), "mm/dd/yyyy hh:MM:ss")}</span>
              </div>

              {user && (
                <div className="flex flex-wrap items-center gap-x-1 min-h-[1.5rem]">
                  <span className="font-medium">By:</span>
                  <Link
                    to={`/admin/user/${user.email}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-app-primary hover:underline rounded-md leading-none"
                  >
                    {user.email}
                  </Link>
                </div>
              )}

              {agenticChatId && user && (
                <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 min-h-[1.5rem] max-w-full">
                  <span className="font-medium shrink-0">Chat ID:</span>
                  <Link
                    to={`/admin/user/${user.email}/chats?openChat=${encodeURIComponent(agenticChatId)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-app-primary hover:underline rounded-md font-mono text-sm break-all leading-snug"
                  >
                    {agenticChatId}
                  </Link>
                </div>
              )}

              {invocation.get("langfuseTraceId") && (
                <div className="flex items-center min-h-[1.5rem]">
                  <a
                    href={`https://cloud.langfuse.com/project/clxf4brjw0003y8x6yd7f7apr/traces/${invocation.get("langfuseTraceId")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-app-primary hover:underline rounded-md font-medium leading-none"
                  >
                    Go to Langfuse Trace
                  </a>
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 w-full flex flex-wrap gap-4 items-start">
              {showInvocationGenerationType && (
                <div className="flex-1 min-w-[200px]">
                  <h2 className="text-lg font-semibold text-gray-800">
                    Generation Type: <span className="text-app-primary">{aiFunctionName}</span>
                  </h2>
                </div>
              )}

              {invocation.get("metaData")?.comparisonMode && (
                <span className="inline-flex items-center self-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                  Comparison Mode
                </span>
              )}

              <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 min-w-[250px]">
                <div className="flex flex-col text-xs">
                  <span className="font-semibold text-gray-700">Billing Status</span>
                  <span className="text-gray-500">{billingContext ? formatDateTime(billingContext.created_at) : "Pending"}</span>
                </div>
                {renderBillingBadge()}
              </div>
              <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2 min-w-[250px]">
                <div className="flex flex-col text-xs">
                  <span className="font-semibold text-gray-700">Credits Used</span>
                  <span className="text-gray-500">≈ ${providerCost.toFixed(2)} USD</span>
                </div>
                <div className="text-2xl font-bold text-indigo-700 tabular-nums">{creditsUsed.toLocaleString()}</div>
              </div>
              <InvocationBilling completions={completions} billingContext={billingContext} />
            </div>

            {historyCompaction && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs text-gray-700">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <span className="font-medium text-gray-600 block mb-1">History Compaction</span>
                  <span className={historyCompaction.applied ? "text-blue-800 font-semibold" : "text-gray-600"}>
                    {historyCompaction.applied ? "Applied" : "Not applied"}
                  </span>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <span className="font-medium text-gray-600 block mb-1">Chars Removed</span>
                  <span className="font-semibold text-blue-800 tabular-nums">{(historyCompaction.charsRemoved ?? 0).toLocaleString()}</span>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <span className="font-medium text-gray-600 block mb-1">Messages Dropped</span>
                  <span className="font-semibold text-blue-800 tabular-nums">{(historyCompaction.droppedMessages ?? 0).toLocaleString()}</span>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <span className="font-medium text-gray-600 block mb-1">Budget / Input</span>
                  <span className="font-semibold text-blue-800 tabular-nums">
                    {(historyCompaction.historyCharBudget ?? 0).toLocaleString()} / {(historyCompaction.charsBefore ?? 0).toLocaleString()}
                  </span>
                </div>
              </div>
            )}

            {planExecutionInvocationId && planExecutionInvocationId !== id && (
              <div className="mt-3 border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs text-amber-900 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">Plan execution data source:</span>
                  <CopyToClipboardButton
                    textToCopy={planExecutionInvocationId}
                    className="font-mono"
                  />
                </div>
                <p>
                  This plan approve/resume request can bill and store completions on a dedicated execution invocation.
                  Metrics below marked "plan execution" come from that invocation ID.
                </p>
                {planExecutionBillingContextError && (
                  <p className="text-red-700">
                    Plan execution billing context error: {planExecutionBillingContextError}
                  </p>
                )}
              </div>
            )}

            {!billingContextLoading && billingContext && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-700">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <span className="font-medium text-gray-600 block mb-1">Completed:</span>
                  {formatDateTime(billingContext.completed_at)}
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <span className="font-medium text-gray-600 block mb-1">Had Output:</span>
                  {billingContext.no_output ? "No" : "Yes"}
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <span className="font-medium text-gray-600 block mb-1">Failure:</span>
                  {billingContext.failure_reason || "—"}
                </div>
              </div>
            )}

            {hasCrossInvocationPlanData && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-700">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <span className="font-medium text-gray-600 block mb-1">Plan execution credits:</span>
                  {planExecutionCompletionsLoading ? "Loading…" : planExecutionCreditsUsed.toLocaleString()}
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <span className="font-medium text-gray-600 block mb-1">Plan execution provider cost:</span>
                  {planExecutionCompletionsLoading ? "Loading…" : `≈ $${planExecutionProviderCost.toFixed(2)} USD`}
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <span className="font-medium text-gray-600 block mb-1">Plan execution billing status:</span>
                  {planExecutionBillingContextLoading
                    ? "Loading…"
                    : (planExecutionBillingContext?.status || "—").replace(/_/g, " ").toUpperCase()}
                </div>
              </div>
            )}
          </div>

          {/* Completions Section - Auto Loaded */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Completions</h2>

            {characterPortrait && (
              <div className="flex flex-col md:flex-row gap-6 mb-4">
                <div className="shrink-0 self-start flex flex-col gap-3 w-full max-w-xs">
                  {characterPortrait.imageUrl ? (
                    <a href={characterPortrait.imageUrl} target="_blank" rel="noreferrer" className="block">
                      <img
                        src={characterPortrait.imageUrl}
                        alt="Generated character portrait"
                        className="block w-full rounded-lg border border-gray-200"
                      />
                    </a>
                  ) : (
                    <div className="flex aspect-square w-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-500">
                      No image — generation succeeded but the image wasn't saved
                    </div>
                  )}
                  {characterPortrait.referenceImage?.imageUrl && (
                    <div>
                      <span className="text-xs font-medium text-gray-600 block mb-1">Reference image</span>
                      <a href={characterPortrait.referenceImage.imageUrl} target="_blank" rel="noreferrer" className="block">
                        <img
                          src={characterPortrait.referenceImage.imageUrl}
                          alt="User-supplied reference"
                          className="block w-full aspect-square object-cover rounded-lg border border-gray-200"
                        />
                      </a>
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2 text-sm text-gray-700">
                  <div className="flex flex-wrap gap-2">
                    {characterPortrait.usedFallbackModel && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                        Fallback (Grok)
                      </span>
                    )}
                    {characterPortrait.hasReferenceImage && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                        Reference image used
                      </span>
                    )}
                  </div>
                  <div><span className="font-medium text-gray-600">Credit Cost:</span> {(characterPortrait.creditCost ?? 0).toLocaleString()}</div>
                  <div><span className="font-medium text-gray-600">Saved:</span> {characterPortrait.savedImageId ? "Yes" : "No — persist failed"}</div>
                  <div><span className="font-medium text-gray-600">Model:</span> {characterPortrait.model || "—"}</div>
                  <div><span className="font-medium text-gray-600">Provider:</span> {characterPortrait.provider || "—"}</div>
                  <div><span className="font-medium text-gray-600">Style Preset:</span> {characterPortrait.stylePresetLabel || characterPortrait.stylePresetId || "—"}</div>
                  {characterPortrait.referenceImageGuidance && (
                    <div><span className="font-medium text-gray-600">Reference Guidance:</span> {characterPortrait.referenceImageGuidance}</div>
                  )}
                  {characterPortrait.prompt && (
                    <div className="pt-2">
                      <span className="font-medium text-gray-600 block mb-1">Prompt:</span>
                      <BoxCopy text={characterPortrait.prompt} className="bg-gray-100">
                        <div className="text-gray-800 whitespace-pre-wrap text-xs">{characterPortrait.prompt}</div>
                      </BoxCopy>
                    </div>
                  )}
                </div>
              </div>
            )}

            {completionsLoading || planExecutionCompletionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-3"></div>
                  <p className="text-gray-500">Loading completions...</p>
                </div>
              </div>
            ) : completions.length === 0 && planExecutionCompletions.length === 0 ? (
              characterPortrait ? null : (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center text-gray-500">
                    <p>No completions were recorded for this invocation.</p>
                  </div>
                </div>
              )
            ) : (
              <div className="space-y-4">
                {completions.length === 0 && planExecutionCompletions.length > 0 && (
                  <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-sm text-amber-900">
                    No completions were recorded on invocation <span className="font-mono">{id}</span>.
                    Showing completions from plan execution invocation <span className="font-mono">{planExecutionInvocationId}</span>.
                  </div>
                )}
                {(completions.length > 0 ? completions : planExecutionCompletions).map((completion, i) => {
                  const { temperature, engine, cost, messages, prompt, functionName, intendedEngine, provider } = completion;
                  const completionSummary = getCompletionAdminSummary(completion);
                  const isExpanded = expandedCompletions.has(completion.id);
                  const totalTokens =
                    typeof completion.tokens === "number"
                      ? completion.tokens
                      : (typeof completion.promptTokens === "number" || typeof completion.completionTokens === "number")
                        ? (completion.promptTokens || 0) + (completion.completionTokens || 0)
                        : null;
                  const promptTokens = typeof completion.promptTokens === "number" ? completion.promptTokens : null;
                  const completionTokens = typeof completion.completionTokens === "number" ? completion.completionTokens : null;
                  const completionGenerationType =
                    humanizeCompletionFunctionName(functionName) || aiFunctionName;

                  const outputs = completion.completions;
                  const hasEmptyOutput =
                    !outputs ||
                    !Array.isArray(outputs) ||
                    outputs.length === 0 ||
                    outputs.every(isSemanticallyEmptyCompletionOutput);

                  return (
                    <div
                      className={`border rounded-lg ${hasEmptyOutput ? "border-red-300 bg-red-50" : "border-gray-300 bg-gray-50"}`}
                      key={completion.id}
                    >
                      {/* Completion Header - Always Visible */}
                      <div className={`p-4 border-b ${hasEmptyOutput ? "border-red-300" : "border-gray-300"}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-lg font-semibold text-gray-900">Completion #{i + 1}</h3>
                            <CopyToClipboardButton
                              textToCopy={completion.id}
                              className="text-xs text-gray-500 font-mono"
                            />
                            {hasEmptyOutput && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">⚠️ No Output</span>
                            )}
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">temp: {temperature ?? "N/A"}</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">${cost?.toFixed(4) || "N/A"}</span>
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                              title={
                                promptTokens != null || completionTokens != null
                                  ? `Prompt: ${promptTokens ?? "N/A"}, Completion: ${completionTokens ?? "N/A"}`
                                  : "Token counts unavailable"
                              }
                            >
                              {totalTokens != null ? `${totalTokens.toLocaleString()} tok` : "tok: N/A"}
                            </span>
                            {completionGenerationType && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                                <span className="text-gray-500 mr-1">Generation Type:</span>
                                <span className="text-app-primary font-medium">{completionGenerationType}</span>
                              </span>
                            )}
                            {skippedBillingIds.has(completion.id) && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200"
                                title="This completion was not included in the invocation's billing ledger."
                              >
                                Skipped billing
                              </span>
                            )}
                          </div>
                          <SudoButton
                            variant="tertiary"
                            size="small"
                            onClick={() => toggleCompletionExpanded(completion.id)}
                          >
                            {isExpanded ? "Hide Details" : "Show Details"}
                          </SudoButton>
                        </div>

                        {(completionSummary.headline || completionSummary.detail) && (
                          <div className="mb-3 text-sm text-left">
                            {completionSummary.headline && (
                              <div className="font-medium text-gray-900">{completionSummary.headline}</div>
                            )}
                            {completionSummary.detail && (
                              <div className="text-xs text-gray-600 mt-1">{completionSummary.detail}</div>
                            )}
                          </div>
                        )}

                        <div className="flex gap-3">
                          <CompletionEngine engine={engine} intendedEngine={intendedEngine} provider={provider} />
                          <DraftEditDiffButton key={`${id}:${completion.id}`} completion={completion} />
                        </div>
                      </div>

                      {/* Completion Details - Expandable */}
                      {isExpanded && (
                        <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                          {/* Prompt Section - Collapsible */}
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                              onClick={() => togglePromptExpanded(completion.id)}
                              className="w-full flex items-center justify-between p-3 bg-gray-100 hover:bg-gray-200 transition-colors"
                            >
                              <h4 className="text-md font-semibold text-gray-800">Prompt</h4>
                              <span className="text-gray-500 text-sm">{expandedPrompts.has(completion.id) ? "▼ Hide" : "▶ Show"}</span>
                            </button>
                            {expandedPrompts.has(completion.id) && (
                              <div className="p-3 bg-white">
                                {completion.messages && (
                                  <BoxCopy text={JSON.stringify(messages, null, 2)}>
                                    <div className="space-y-3">
                                      {completion.messages.map((message, idx) => {
                                        return (
                                          <div
                                            key={idx}
                                            className="border-l-4 border-blue-200 pl-4 py-2"
                                          >
                                            <div className="font-semibold text-blue-800 mb-1">{message.role}:</div>
                                            <div className="text-gray-800 whitespace-pre-wrap">{message.content}</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </BoxCopy>
                                )}
                                {prompt && (
                                  <BoxCopy text={prompt}>
                                    <div className="text-gray-800 whitespace-pre-wrap">{completion.prompt}</div>
                                  </BoxCopy>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Output Section - Collapsible */}
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                              onClick={() => toggleOutputExpanded(completion.id)}
                              className="w-full flex items-center justify-between p-3 bg-gray-100 hover:bg-gray-200 transition-colors"
                            >
                              <h4 className="text-md font-semibold text-gray-800">Output</h4>
                              <span className="text-gray-500 text-sm">{expandedOutputs.has(completion.id) ? "▼ Hide" : "▶ Show"}</span>
                            </button>
                            {expandedOutputs.has(completion.id) && (
                              <div className="p-3 bg-white space-y-3">
                                {completion.completions.map((c, idx) => {
                                  return (
                                    <BoxCopy
                                      key={idx}
                                      text={c}
                                    >
                                      <div className="text-gray-800 whitespace-pre-wrap">{c}</div>
                                    </BoxCopy>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {invocation.get("metaData")?.writePovTense && (
            <details className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <summary className="text-xl font-semibold text-gray-900 cursor-pointer">Write POV / tense resolution</summary>
              <p className="text-sm text-gray-600 my-3">
                Settings read during this generation and the source used for each field.
                Client values are reported store state, not proof that the visible controls finished loading.
              </p>
              <JSONViewer data={invocation.get("metaData").writePovTense} />
            </details>
          )}

          {invocation.get("metaData")?.writeGeneration && (
            <details className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <summary className="text-xl font-semibold text-gray-900 cursor-pointer">Write style, context, and settings</summary>
              <p className="text-sm text-gray-600 my-3">
                Initial prepared inference only; retries and fallbacks may differ. Context counts describe
                available template inputs, not proof that conditional templates included every section.
              </p>
              <JSONViewer data={invocation.get("metaData").writeGeneration} />
            </details>
          )}

          {/* Context Section - Lazy Loaded */}
          {invocationContext && Object.keys(invocationContext).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Invocation Context</h2>
                {!showContext && (
                  <SudoButton
                    variant="secondary"
                    size="small"
                    onClick={() => setShowContext(true)}
                  >
                    Load Context
                  </SudoButton>
                )}
              </div>

              {!showContext ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Click "Load Context" to view context data</p>
                </div>
              ) : (
                <JSONViewer data={invocationContext} />
              )}
            </div>
          )}

          {/* Stream Replay Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Stream Replay</h2>
            <StreamReplay invocationId={id} />
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component for copy functionality
const BoxCopy = ({ text, children, className = "bg-white" }) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <div className={`relative border border-gray-300 rounded-lg p-4 shadow-sm ${className}`}>
      <button
        onClick={handleCopy}
        className={`absolute top-3 right-3 px-3 py-1 text-xs font-medium rounded transition-colors ${
          isCopied ? "bg-app-primary border border-app-primary text-white" : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
        }`}
      >
        {isCopied ? "Copied!" : "Copy"}
      </button>
      <div className="pr-16">{children}</div>
    </div>
  );
};

export default Invocation;
